import { useTranslation } from 'react-i18next';

import type { Grade } from './site.js';

const GRADES: readonly Grade[] = ['A', 'B', 'C', 'D', 'E', 'F'];

/** Lowest score of each grade (monitoring-rules §6). */
export const GRADE_FLOOR: Record<Grade, number> = { A: 90, B: 80, C: 65, D: 50, E: 35, F: 0 };

/** The grade a score falls in, for its colour (an area has a score, not a grade of its own). */
export const gradeOfScore = (score: number): Grade =>
  GRADES.find((g) => score >= GRADE_FLOOR[g]) ?? 'F';

/** The grade as the big ring of the mock-up (components.css .ring), the score beside it. */
export function GradeRing({ score, grade }: { score: number; grade: Grade }) {
  const { t } = useTranslation();
  const length = 2 * Math.PI * 42;
  return (
    <svg viewBox="0 0 100 100" role="img" aria-label={t('overview.card.scoreSr', { score, grade })}>
      <circle cx="50" cy="50" r="42" fill="none" stroke="var(--line-strong)" strokeWidth="6" />
      <circle
        cx="50"
        cy="50"
        r="42"
        fill="none"
        stroke={`var(--g-${grade.toLowerCase()})`}
        strokeWidth="6"
        strokeDasharray={`${(Math.max(0, Math.min(100, score)) / 100) * length} ${length}`}
        transform="rotate(-90 50 50)"
      />
      <text
        x="50"
        y="60"
        textAnchor="middle"
        fontSize="34"
        fontWeight="800"
        fill="currentColor"
        fontFamily="Manrope, sans-serif"
      >
        {grade}
      </text>
    </svg>
  );
}

/** A score in a ring of its grade's colour, the number inside (site-area.html .ring). */
export function ScoreRing({ score, label }: { score: number; label: string }) {
  const length = 2 * Math.PI * 42;
  return (
    <svg viewBox="0 0 100 100" role="img" aria-label={label}>
      <circle cx="50" cy="50" r="42" fill="none" stroke="var(--line-strong)" strokeWidth="6" />
      <circle
        cx="50"
        cy="50"
        r="42"
        fill="none"
        stroke={`var(--g-${gradeOfScore(score).toLowerCase()})`}
        strokeWidth="6"
        strokeDasharray={`${(Math.max(0, Math.min(100, score)) / 100) * length} ${length}`}
        transform="rotate(-90 50 50)"
      />
      <text
        x="50"
        y="58"
        textAnchor="middle"
        fontSize="22"
        fontWeight="800"
        fill="currentColor"
        fontFamily="Manrope, sans-serif"
      >
        {score}
      </text>
    </svg>
  );
}

/**
 * The scale A to F with the grade marked (components.css .grades), hidden from screen readers: the
 * ring reads the grade out.
 */
export function GradeSteps({ grade }: { grade: Grade }) {
  return (
    <div className="grades max-w-[360px]" aria-hidden="true">
      {GRADES.map((g) => (
        <span key={g} className={`${g.toLowerCase()}${g === grade ? ' on' : ''}`}>
          {g}
        </span>
      ))}
    </div>
  );
}
